import { Stack, Typography, useTheme } from "@mui/material";
import { RootState } from "src/store";
import { connect } from "react-redux";
import { Button } from "@itk/mui-components";
import { genericTranslationsK, useGenericTranslationsT } from "src/translation-hooks/generic-translations";
import { addEditLabelActions } from "./add-edit-label-store";
import { useParams } from "react-router";
import { LabelsMultiSelectChip } from "../labels-multi-select-chip";
import { useMemo, useRef, useState } from "react";
import { LabelsMultiSelectChipRef } from "../labels-multi-select-chip/labels-multi-select-chip-types";
import { configuratorActions } from "../../configurator-store";
import { canItemHaveLabels } from "../../configurator-utils";
import { DekoxIcon } from "src/components";
import { useConfiguratorPageT } from "src/translation-hooks/configurator-page";

const mapStateToProps = (state: RootState) => ({
  isShowingAddEditLabel: state.pageReducer.combinedConfiguratorReducer.addEditLabelReducer.isShowingAddEditLabel,
  activeTab: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.activeTab,
  selectedConfigurations: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.selectedConfigurations,
  selectedProducts: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.selectedProducts,
  configurationsState: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.configurationsState
});

const ownActions = {
  setIsShowingAddEditLabel: addEditLabelActions.setIsShowingAddEditLabel,
  setSelectedConfigurations: configuratorActions.setSelectedConfigurations,
  setSelectedProducts: configuratorActions.setSelectedProducts
};

type Props = typeof ownActions & ReturnType<typeof mapStateToProps>;

const AddEditLabelBase = (props: Props) => {
  const { isShowingAddEditLabel, setIsShowingAddEditLabel, activeTab, selectedConfigurations, setSelectedConfigurations, selectedProducts, setSelectedProducts, configurationsState } = props;

  const theme = useTheme();
  const { projectId = "" } = useParams<{ projectId: string }>();
  const genericTranslationsT = useGenericTranslationsT();
  const configuratorPageT = useConfiguratorPageT();

  const labelsMultiChipRef = useRef<LabelsMultiSelectChipRef>(null);
  const [isLabelsLoading, setIsLabelsLoading] = useState(false);

  // One dialog serves both tabs, but each tab's selection targets a different entity
  const isProductsTab = activeTab === "PRODUCTS";

  // Products can be selected at any depth, but only the top two levels carry labels. The rest are
  // dropped here rather than posted, and reported below so the count the user sees matches what will
  // actually be written. An id with no matching item counts as skipped too.
  const { labelableProductIds, skippedProductCount } = useMemo(() => {
    if (!isProductsTab) return { labelableProductIds: [], skippedProductCount: 0 };

    const itemsById = new Map(
      (configurationsState?.configurations ?? [])
        .flatMap(configuration => configuration.systemProductConfigurationItems ?? [])
        .map(item => [item.systemProductConfigurationItemId ?? "", item])
    );

    const labelable = selectedProducts.filter(productId => {
      const item = itemsById.get(productId);
      return item ? canItemHaveLabels(item) : false;
    });

    return { labelableProductIds: labelable, skippedProductCount: selectedProducts.length - labelable.length };
  }, [isProductsTab, selectedProducts, configurationsState]);

  const entityIds = isProductsTab ? labelableProductIds : selectedConfigurations;
  const clearSelection = isProductsTab ? setSelectedProducts : setSelectedConfigurations;

  if (!isShowingAddEditLabel) return null;

  return (
    <Stack
      overflow="hidden"
      position="absolute"
      bottom={4}
      right={10}
      width={400}
      bgcolor={theme.palette.customColors.white}
      border={`1px solid ${theme.palette.customColors.grey}`}
      borderRadius={2}
      sx={{ boxShadow: "0px 4px 20px rgba(0,0,0,0.12)" }}
    >
      <Stack px={3} py={2} gap={1}>
        <LabelsMultiSelectChip ref={labelsMultiChipRef} projectId={projectId} entityIds={entityIds} onLoadingChange={setIsLabelsLoading} />
        {skippedProductCount > 0 && (
          <Stack direction="row" alignItems="flex-start" gap={0.5}>
            <DekoxIcon icon="info" fontSize="small" color="warning" />
            <Typography variant="caption" color={theme.palette.customColors.darkGrey}>
              {configuratorPageT.labelsApplyToTopLevelProductsOnly(skippedProductCount.toString(), selectedProducts.length.toString())}
            </Typography>
          </Stack>
        )}
      </Stack>
      <Stack direction="row" alignItems="center" justifyContent="space-between" px={3} py={1.5} bgcolor={theme.palette.customColors.white} borderTop={`1px solid ${theme.palette.customColors.grey}`}>
        <Typography>{genericTranslationsT.selected(entityIds.length.toString())}</Typography>
        <Stack direction="row" alignContent="center" gap={2}>
          <Button
            disabled={isLabelsLoading}
            t={genericTranslationsT}
            k={genericTranslationsK}
            field="cancel"
            variant="outlined"
            onClick={() => {
              setIsShowingAddEditLabel(false);
            }}
          />
          <Button
            disabled={isLabelsLoading || entityIds.length === 0}
            loading={isLabelsLoading}
            t={genericTranslationsT}
            k={genericTranslationsK}
            field="apply"
            variant="contained"
            onClick={() => {
              labelsMultiChipRef.current?.handleApply(() => {
                clearSelection([]);
                setIsShowingAddEditLabel(false);
              });
            }}
          />
        </Stack>
      </Stack>
    </Stack>
  );
};
export const AddEditLabel = connect(mapStateToProps, ownActions)(AddEditLabelBase);
